import entries from 'lodash/entries';
import api from './api';
import axios from 'axios';

const AWS_CDN = import.meta.env.VITE_AWS_CDN;

// Main Service Class
class VocabularyService {
    // Vocabulary Words Methods
    async getWords(filters?: VocabularyFilters, pagination?: PaginationParams): Promise<VocabularyResponse> {
        const params = new URLSearchParams();

        if (filters?.search) params.append('search', filters.search);
        if (filters?.difficulty) params.append('difficulty', filters.difficulty);
        if (filters?.category) params.append('category', filters.category);
        if (filters?.part_of_speech) params.append('part_of_speech', filters.part_of_speech);
        if (filters?.isFavorite !== undefined) params.append('is_favorite', filters.isFavorite.toString());
        if (filters?.isBookmarked !== undefined) params.append('is_bookmarked', filters.isBookmarked.toString());
        if (filters?.groupId) params.append('group_id', filters.groupId);
        if (filters?.masteryLevel?.min !== undefined) params.append('mastery_min', filters.masteryLevel.min.toString());
        if (filters?.masteryLevel?.max !== undefined) params.append('mastery_max', filters.masteryLevel.max.toString());

        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(`/vocabulary/${params.toString() ? '?' + params.toString() : ''}`);
        return response.data;
    }

    async getWordById(id: string): Promise<VocabularyWord> {
        const response = await api.get(`/vocabulary/details/${id}/`);
        return response.data;
    }

    async createWord(word: CreateWordRequest): Promise<VocabularyWord> {
        const response = await api.post('/vocabulary/', word);
        return response.data;
    }

    async createBulkWords(words: CreateWordRequest[]): Promise<VocabularyWord[]> {
        const response = await api.post('/vocabulary/bulk/', { words });
        return response.data;
    }

    async updateWord(word: UpdateWordRequest): Promise<VocabularyWord> {
        const response = await api.put(`/vocabulary/${word.id}/`, word);
        return response.data;
    }

    async deleteWord(id: string): Promise<void> {
        await api.delete(`/vocabulary/${id}/`);
    }

    async toggleFavorite(id: string): Promise<VocabularyWord> {
        const response = await api.patch(`/vocabulary/${id}/toggle-favorite/`);
        return response.data;
    }

    async toggleBookmark(id: string): Promise<VocabularyWord> {
        const response = await api.patch(`/vocabulary/${id}/toggle-bookmark/`);
        return response.data;
    }

    // Categories Methods
    async getCategories(): Promise<VocabularyCategoryResponse[]> {
        const response = await api.get('/vocabulary/category/');
        return response.data;
    }

    async getVocabularyCategories(): Promise<VocabularyCategoryResponse[]> {
        const response = await api.get('/vocabulary/category/');
        return response.data;
    }

    // Enhanced Vocabulary Groups Methods
    async getVocabularyGroups(filters?: VocabularyGroupFilters): Promise<VocabularyGroupResponse> {
        const params = new URLSearchParams();

        if (filters?.key_word) params.append('key_word', filters.key_word);
        if (filters?.privacy) params.append('privacy', filters.privacy);

        if (filters?.page) params.append('page', filters.page.toString());
        if (filters?.page_size) params.append('page_size', filters.page_size.toString());

        const response = await api.get(
            `/vocabulary/vocabulary-groups/${params.toString() ? '?' + params.toString() : ''}`,
        );
        return response.data;
    }

    async getVocabularyGroupById(id: string): Promise<VocabularyGroup> {
        const response = await api.get(`/vocabulary/vocabulary-groups/${id}/`);
        return response.data;
    }

    async createVocabularyGroup(group: CreateVocabularyGroupRequest): Promise<VocabularyGroup> {
        const response = await api.post('/vocabulary/vocabulary-groups/', group);
        return response.data;
    }

    async updateVocabularyGroup(group: UpdateVocabularyGroupRequest): Promise<VocabularyGroup> {
        const { id, ...updateData } = group;
        const response = await api.put(`/vocabulary/vocabulary-groups/${id}/`, updateData);
        return response.data;
    }

    async deleteVocabularyGroup(id: string): Promise<void> {
        await api.delete(`/vocabulary/vocabulary-groups/${id}/`);
    }

    async addWordsToVocabularyGroup(groupId: string, wordIds: number[]): Promise<VocabularyGroup> {
        const response = await api.post(`/vocabulary/vocabulary-groups/${groupId}/add-words/`, { word_ids: wordIds });
        return response.data;
    }

    async removeWordsFromVocabularyGroup(groupId: string, wordIds: number[]): Promise<VocabularyGroup> {
        const response = await api.post(`/vocabulary/vocabulary-groups/${groupId}/remove-words/`, {
            word_ids: wordIds,
        });
        return response.data;
    }

    async getWordsInVocabularyGroup(groupId: string, pagination?: PaginationParams): Promise<VocabularyResponse> {
        const params = new URLSearchParams();
        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(
            `/vocabulary/vocabulary-groups/${groupId}/words${params.toString() ? '?' + params.toString() : ''}/`,
        );
        return response.data;
    }

    // Group Sharing Methods
    async shareVocabularyGroup(
        groupId: string,
        userId: number,
        permission: 'view' | 'edit' | 'admin',
    ): Promise<VocabularyGroupShare> {
        const response = await api.post(`/vocabulary/vocabulary-groups/${groupId}/share/`, {
            user_id: userId,
            permission,
        });
        return response.data;
    }

    async getGroupShares(groupId: string): Promise<VocabularyGroupShare[]> {
        const response = await api.get(`/vocabulary-groups/${groupId}/shares/`);
        return response.data;
    }

    // Vocabulary Sets Methods
    async getVocabularySets(
        filters?: VocabularySetFilters,
        pagination?: PaginationParams,
    ): Promise<VocabularySetResponse> {
        const params = new URLSearchParams();

        if (filters?.search) params.append('search', filters.search);
        if (filters?.is_public !== undefined) params.append('is_public', filters.is_public.toString());
        if (filters?.owner) params.append('owner', filters.owner.toString());
        if (filters?.difficulty) params.append('difficulty', filters.difficulty);
        if (filters?.language) params.append('language', filters.language);
        if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
        if (filters?.min_words) params.append('min_words', filters.min_words.toString());
        if (filters?.max_words) params.append('max_words', filters.max_words.toString());
        if (filters?.source) params.append('source', filters.source);

        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(`/vocabulary-sets${params.toString() ? '?' + params.toString() : ''}/`);
        return response.data;
    }

    async getVocabularySetById(id: string): Promise<VocabularySet> {
        const response = await api.get(`/vocabulary-sets/${id}/`);
        return response.data;
    }

    async createVocabularySet(set: CreateVocabularySetRequest): Promise<VocabularySet> {
        const response = await api.post('/vocabulary-sets', set);
        return response.data;
    }

    async updateVocabularySet(set: UpdateVocabularySetRequest): Promise<VocabularySet> {
        const { id, ...updateData } = set;
        const response = await api.put(`/vocabulary-sets/${id}`, updateData);
        return response.data;
    }

    async deleteVocabularySet(id: string): Promise<void> {
        await api.delete(`/vocabulary-sets/${id}/`);
    }

    // Study Session Methods
    async createStudySession(session: CreateStudySessionRequest): Promise<StudySession> {
        const response = await api.post('/vocabulary/study-sessions/start_session/', session);
        return response.data;
    }

    async completeStudySession(sessionId: string, completionData?: CompleteStudySessionRequest): Promise<StudySessionCompletionResponse> {
        const response = await api.post(`/vocabulary/study-sessions/${sessionId}/complete_session/`, completionData);
        return response.data;
    }

    async submitVocabularyAnswer(answerData: SubmitVocabularyAnswerRequest): Promise<VocabularyAttempt> {
        const formData = new FormData();
        for (const [key, value] of entries(answerData)) {
            formData.append(key, value);
        }

        const response = await api.post('/vocabulary/study-sessions/submit_answer/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async updateStudySession(session: UpdateStudySessionRequest): Promise<StudySession> {
        const { id, ...updateData } = session;
        const response = await api.put(`/study-sessions/${id}`, updateData);
        return response.data;
    }

    async getStudySessions(userId?: number, groupId?: string): Promise<StudySession[]> {
        const params = new URLSearchParams();
        if (userId) params.append('user', userId.toString());
        if (groupId) params.append('group', groupId);

        const response = await api.get(`/study-sessions${params.toString() ? '?' + params.toString() : ''}/`);
        return response.data;
    }

    // Group Collection Methods
    async getGroupCollections(pagination?: PaginationParams): Promise<GroupCollection[]> {
        const params = new URLSearchParams();
        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(`/group-collections${params.toString() ? '?' + params.toString() : ''}/`);
        return Array.isArray(response.data) ? response.data : response.data.results || [];
    }

    async createGroupCollection(collection: CreateGroupCollectionRequest): Promise<GroupCollection> {
        const response = await api.post('/group-collections', collection);
        return response.data;
    }

    async updateGroupCollection(collection: UpdateGroupCollectionRequest): Promise<GroupCollection> {
        const { id, ...updateData } = collection;
        const response = await api.put(`/group-collections/${id}`, updateData);
        return response.data;
    }

    async deleteGroupCollection(id: string): Promise<void> {
        await api.delete(`/group-collections/${id}/`);
    }

    // Legacy FlashcardGroup Methods for backward compatibility
    async getFlashcardGroups(
        filters?: FlashcardGroupFilters,
        pagination?: PaginationParams,
    ): Promise<FlashcardGroupResponse> {
        // Map to new vocabulary groups endpoint
        const groupFilters: VocabularyGroupFilters = {
            key_word: filters?.key_word,
        };

        const response = await this.getVocabularyGroups(groupFilters);

        // Convert to legacy format
        const legacyGroups: FlashcardGroup[] = response.results.map((group) => ({
            id: parseInt(group.id, 16), // Convert UUID to number for legacy compatibility
            name: group.name,
            description: group.description,
            color: group.color,
            icon: group.icon,
            isDefault: group.is_default,
            createdAt: group.created_at,
            updatedAt: group.updated_at,
            vocabularyIds: [], // Would need to fetch from memberships
            settings: {
                studyOrder: group.study_order as any,
                showPronunciation: group.show_pronunciation,
                showExample: group.show_example,
                autoAdvance: group.auto_advance,
                reviewMode: group.review_mode as any,
            },
        }));

        return {
            groups: legacyGroups,
            total: response.count,
            page: pagination?.page || 1,
            totalPages: Math.ceil(response.count / (pagination?.limit || 20)),
        };
    }

    async getFavoriteWords(pagination?: PaginationParams): Promise<VocabularyResponse> {
        return await this.getWords({ isFavorite: true }, pagination);
    }

    async getBookmarkedWords(pagination?: PaginationParams): Promise<VocabularyResponse> {
        return await this.getWords({ isBookmarked: true }, pagination);
    }

    async getPartsOfSpeech(): Promise<string[]> {
        return ['noun', 'verb', 'adjective', 'adverb', 'pronoun', 'preposition', 'conjunction', 'interjection'];
    }

    // === ENHANCED WORD SAVING METHODS ===

    async getSavedWords(filters?: SavedWordFilters, pagination?: PaginationParams): Promise<SavedWordResponse> {
        const params = new URLSearchParams();

        if (filters?.groupId) params.append('group_id', filters.groupId);
        if (filters?.studyPriority) params.append('study_priority', filters.studyPriority);
        if (filters?.masteryLevel !== undefined) params.append('mastery_level', filters.masteryLevel.toString());
        if (filters?.hasNotes !== undefined) params.append('has_notes', filters.hasNotes.toString());
        if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
        if (filters?.lastStudiedBefore) params.append('last_studied_before', filters.lastStudiedBefore);
        if (filters?.lastStudiedAfter) params.append('last_studied_after', filters.lastStudiedAfter);

        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(`/vocabulary/saved-words${params.toString() ? '?' + params.toString() : ''}/`);
        return response.data;
    }

    async saveWord(data: CreateSavedWordRequest): Promise<SavedWord> {
        const response = await api.post('/vocabulary/saved-words/', data);
        return response.data;
    }

    async updateSavedWord(data: UpdateSavedWordRequest): Promise<SavedWord> {
        const { id, ...updateData } = data;
        const response = await api.put(`/vocabulary/saved-words/${id}/`, updateData);
        return response.data;
    }

    async removeSavedWord(id: string): Promise<void> {
        await api.delete(`/vocabulary/saved-words/${id}/`);
    }

    async getSavedWordById(id: string): Promise<SavedWord> {
        const response = await api.get(`/vocabulary/saved-words/${id}/`);
        return response.data;
    }

    async bulkSaveWords(wordIds: string[], groupId?: string): Promise<SavedWord[]> {
        const response = await api.post('/vocabulary/saved-words/bulk/', {
            word_ids: wordIds,
            group_id: groupId,
        });
        return response.data;
    }

    async bulkRemoveSavedWords(wordIds: string[]): Promise<void> {
        await api.post('/vocabulary/saved-words/bulk-remove/', {
            word_ids: wordIds,
        });
    }

    // === MINIGAME SESSION MANAGEMENT ===

    async createMinigameSession(data: CreateMinigameSessionRequest): Promise<MinigameSession> {
        const response = await api.post('/vocabulary/minigame-sessions/', data);
        return response.data;
    }

    async updateMinigameSession(data: UpdateMinigameSessionRequest): Promise<MinigameSession> {
        const { id, ...updateData } = data;
        const response = await api.put(`/vocabulary/minigame-sessions/${id}/`, updateData);
        return response.data;
    }

    async getMinigameSession(id: string): Promise<MinigameSession> {
        const response = await api.get(`/vocabulary/minigame-sessions/${id}/`);
        return response.data;
    }

    async getMinigameSessions(
        gameType?: MinigameSession['gameType'],
        pagination?: PaginationParams,
    ): Promise<{ results: MinigameSession[]; count: number; next?: string; previous?: string }> {
        const params = new URLSearchParams();

        if (gameType) params.append('game_type', gameType);
        if (pagination?.page) params.append('page', pagination.page.toString());
        if (pagination?.limit) params.append('page_size', pagination.limit.toString());

        const response = await api.get(
            `/vocabulary/minigame-sessions${params.toString() ? '?' + params.toString() : ''}/`,
        );
        return response.data;
    }

    async getMinigameStats(gameType?: MinigameSession['gameType']): Promise<MinigameStats[]> {
        const params = new URLSearchParams();
        if (gameType) params.append('game_type', gameType);

        const response = await api.get(
            `/vocabulary/minigame-stats${params.toString() ? '?' + params.toString() : ''}/`,
        );
        return response.data;
    }

    async generateMinigameWords(
        gameType: MinigameSession['gameType'],
        difficulty: 'easy' | 'medium' | 'hard',
        count: number = 10,
        groupId?: string,
    ): Promise<VocabularyWord[]> {
        const response = await api.post('/vocabulary/generate-minigame-words/', {
            game_type: gameType,
            difficulty,
            count,
            group_id: groupId,
        });
        return response.data;
    }

    // === PROGRESS TRACKING ===

    async updateStudyProgress(data: UpdateStudyProgressRequest): Promise<StudyProgress> {
        const response = await api.post('/vocabulary/study-progress/', data);
        return response.data;
    }

    async getStudyProgress(wordId: string): Promise<StudyProgress> {
        const response = await api.get(`/vocabulary/study-progress/${wordId}/`);
        return response.data;
    }

    async getUserStudyProgress(filters?: {
        masteryLevel?: number;
        lastStudiedBefore?: string;
    }): Promise<StudyProgress[]> {
        const params = new URLSearchParams();

        if (filters?.masteryLevel !== undefined) params.append('mastery_level', filters.masteryLevel.toString());
        if (filters?.lastStudiedBefore) params.append('last_studied_before', filters.lastStudiedBefore);

        const response = await api.get(
            `/vocabulary/user-study-progress${params.toString() ? '?' + params.toString() : ''}/`,
        );
        return response.data;
    }

    async getWordsForReview(limit: number = 20): Promise<VocabularyWord[]> {
        const response = await api.get(`/vocabulary/words-for-review/?limit=${limit}`);
        return response.data;
    }

    // === ENHANCED GROUP MANAGEMENT ===

    async reorderWordsInGroup(groupId: string, wordOrders: { wordId: string; order: number }[]): Promise<void> {
        await api.post(`/vocabulary/vocabulary-groups/${groupId}/reorder-words/`, {
            word_orders: wordOrders,
        });
    }

    async moveWordsBetweenGroups(sourceGroupId: string, targetGroupId: string, wordIds: string[]): Promise<void> {
        await api.post(`/vocabulary/vocabulary-groups/move-words/`, {
            source_group_id: sourceGroupId,
            target_group_id: targetGroupId,
            word_ids: wordIds,
        });
    }

    async duplicateGroup(groupId: string, newName: string): Promise<VocabularyGroup> {
        const response = await api.post(`/vocabulary/vocabulary-groups/${groupId}/duplicate/`, {
            name: newName,
        });
        return response.data;
    }

    // === SESSION INTEGRATION ===

    async linkWordsToSession(sessionId: string, wordIds: string[]): Promise<void> {
        await api.post(`/sessions/${sessionId}/link-vocabulary/`, {
            word_ids: wordIds,
        });
    }

    async getSessionVocabulary(sessionId: string): Promise<VocabularyWord[]> {
        const response = await api.get(`/sessions/${sessionId}/vocabulary/`);
        return response.data;
    }

    async updateSessionVocabularyProgress(
        sessionId: string,
        wordId: string,
        progress: {
            isCorrect: boolean;
            timeSpent: number;
            attempts: number;
        },
    ): Promise<void> {
        await api.post(`/sessions/${sessionId}/vocabulary/${wordId}/progress/`, progress);
    }

    async getTranscriptData(id: string): Promise<any> {
        const response = await axios.get(AWS_CDN + `/word_timings/${id}`);
        return response.data;
    }
}

// Unified data structure - works for both backend and frontend
export interface VocabularyWord {
    id: number;
    word: string;
    pronunciation?: string;
    definition?: string;
    example?: string;
    part_of_speech?: string;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    category?: number; // Foreign key to CategoryVocabulary
    isBookmarked: boolean;
    isFavorite: boolean;
    masteryLevel: number;
    audio_url?: string;
    image_url?: string;
    synonyms: string[]; // From serializer
    antonyms: string[]; // From serializer
    created_by?: number; // User ID who created this word
    created_at?: string;
    updated_at?: string;
}

export interface VocabularyCategory {
    name: string; // Primary key
    label?: string;
    icon?: string;
    count?: number; // From serializer
}

export interface VocabularyCategoryResponse {
    results: VocabularyCategory[];
    count: number;
    next?: string;
    previous?: string;
}

// Enhanced Vocabulary Group Models matching backend structure
export interface VocabularyGroup {
    id: string; // UUID field
    name: string;
    description?: string;
    color?: string;
    icon?: string;

    // Ownership and Privacy
    owner: number; // User ID
    privacy: 'public' | 'private' | 'unlisted';
    is_default: boolean;

    // Group Settings
    study_order: 'sequential' | 'random' | 'difficulty' | 'mastery' | 'alphabetical';
    show_pronunciation: boolean;
    show_example: boolean;
    auto_advance: boolean;
    review_mode: 'flashcard' | 'quiz' | 'mixed' | 'speaking' | 'listening';

    // Study Plan Settings
    daily_target: number;
    weekly_goal: number;

    // Statistics (calculated fields)
    total_words: number;
    mastered_words: number;
    learning_words: number;
    new_words: number;

    // Progress tracking
    progress: {
        daily: number[];
        weekly: number[];
        monthly: number[];
        mastery_level: number;
        study_streak: number;
        last_studied?: string;
    };

    // Metadata
    language: string;
    source?: string;
    tags: string[];

    // Timestamps
    created_at: string;
    updated_at: string;
    last_studied?: string;

    // Computed properties
    word_count?: number;
    average_mastery_level?: number;
    study_streak?: number;

    downloads?: number;
    likes?: number;
}

export interface VocabularyGroupMembership {
    id: number;
    group: string; // Group UUID
    vocabulary: number; // Vocabulary ID

    // Word-specific progress in this group
    mastery_level: number;
    correct_attempts: number;
    wrong_attempts: number;
    last_practiced?: string;
    next_review?: string;

    // Status in this group
    status: 'new' | 'learning' | 'reviewing' | 'mastered' | 'struggling';

    // Ordering and custom settings
    order_index: number;
    is_favorite_in_group: boolean;
    notes?: string;

    // Timestamps
    added_at: string;
    updated_at: string;

    // Computed properties
    accuracy_rate?: number;
}

export interface VocabularyGroupShare {
    id: number;
    group: string; // Group UUID
    user: number; // User ID
    permission: 'view' | 'edit' | 'admin';

    // Access control
    can_edit: boolean;
    can_add_words: boolean;
    can_remove_words: boolean;
    can_share: boolean;

    // Sharing metadata
    shared_by: number; // User ID
    shared_at: string;
    expires_at?: string;

    // Computed properties
    is_expired?: boolean;
}

export interface VocabularySet {
    id: string; // UUID field
    name: string;
    description?: string;

    // Ownership and Privacy
    owner: number; // User ID
    is_public: boolean;

    // Metadata
    difficulty: 'beginner' | 'intermediate' | 'advanced' | 'mixed';
    language: string;
    source?: string;
    tags: string[];

    // Statistics
    total_words: number;
    downloads: number;
    likes: number;

    // Timestamps
    created_at: string;
    updated_at: string;

    // Computed properties
    word_count?: number;
}

export interface StudySession {
    id: string; // UUID field
    user: number; // User ID
    group: string; // Group UUID

    // Session Details
    session_type: 'flashcard' | 'quiz' | 'review' | 'test' | 'practice';
    start_time: string;
    end_time?: string;
    total_time: number; // in seconds

    // Performance Metrics
    words_studied: number[]; // List of vocabulary IDs
    words_correct: number[]; // List of vocabulary IDs
    words_incorrect: number[]; // List of vocabulary IDs
    accuracy: number; // percentage

    // Session State
    is_completed: boolean;
    current_word_index: number;

    // Computed properties
    duration_minutes?: number;
    words_studied_count?: number;
}

export interface GroupCollection {
    id: string; // UUID field
    name: string;
    description?: string;

    // Ownership
    owner: number; // User ID
    is_public: boolean;

    // Metadata
    category?: string;
    tags: string[];

    // Statistics
    total_groups: number;
    total_words: number;

    // Timestamps
    created_at: string;
    updated_at: string;

    // Computed properties
    group_count?: number;
    word_count?: number;
}

// Legacy FlashcardGroup for backward compatibility
export interface FlashcardGroup {
    id: number;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
    vocabularyIds: number[];
    settings: {
        studyOrder: 'sequential' | 'random' | 'difficulty' | 'mastery';
        showPronunciation: boolean;
        showExample: boolean;
        autoAdvance: boolean;
        reviewMode: 'flashcard' | 'quiz' | 'mixed';
    };
}

// Request/Response Interfaces

// Vocabulary Group Requests
export interface CreateVocabularyGroupRequest {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    privacy?: 'public' | 'private' | 'unlisted';
    vocabularyIds?: number[];
    study_order?: 'sequential' | 'random' | 'difficulty' | 'mastery' | 'alphabetical';
    show_pronunciation?: boolean;
    show_example?: boolean;
    auto_advance?: boolean;
    review_mode?: 'flashcard' | 'quiz' | 'mixed' | 'speaking' | 'listening';
    daily_target?: number;
    weekly_goal?: number;
    language?: string;
    source?: string;
    tags?: string[];
}

export interface UpdateVocabularyGroupRequest extends Partial<CreateVocabularyGroupRequest> {
    id: string;
}

export interface VocabularyGroupFilters {
    key_word?: string;
    privacy?: string;
    page?: number;
    page_size?: number;
}

export interface VocabularyGroupResponse {
    results: VocabularyGroup[];
    count: number;
    next?: string;
    previous?: string;
}

// Vocabulary Set Requests
export interface CreateVocabularySetRequest {
    name: string;
    description?: string;
    vocabularyIds: number[];
    is_public?: boolean;
    difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'mixed';
    language?: string;
    source?: string;
    tags?: string[];
}

export interface UpdateVocabularySetRequest extends Partial<CreateVocabularySetRequest> {
    id: string;
}

export interface VocabularySetFilters {
    search?: string;
    is_public?: boolean;
    owner?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'mixed';
    language?: string;
    tags?: string[];
    min_words?: number;
    max_words?: number;
    source?: string;
}

export interface VocabularySetResponse {
    results: VocabularySet[];
    count: number;
    next?: string;
    previous?: string;
}

// Study Session Requests
export interface CreateStudySessionRequest {
    base_session_id: string;
    vocabulary_group_id: string; // Group UUID
    session_type?: 'flashcard' | 'quiz' | 'review' | 'test' | 'practice';
}

export interface UpdateStudySessionRequest {
    id: string;
    end_time?: string;
    total_time?: number;
    words_studied?: number[];
    words_correct?: number[];
    words_incorrect?: number[];
    accuracy?: number;
    is_completed?: boolean;
    current_word_index?: number;
}

export interface CompleteStudySessionRequest {
    attempt_data?: {
        vocabulary_id: string;
        answer?: string;
        is_correct?: boolean;
        score?: number;
        max_score?: number;
        feedback?: any;
        audio_record_url?: string;
        audio_duration?: number;
        audio_format?: string;
    };
}

export interface SubmitVocabularyAnswerRequest {
    session_id: string;
    vocabulary_id: string;
    answer?: string;
    audio_duration?: number;
    audio_format?: string;
    audio_file?: File;
}

export interface VocabularyAttempt {
    id: string;
    session: string;
    vocabulary: string;
    user: number;
    attempt_number: number;
    answer: string;
    is_correct: boolean;
    score: number;
    max_score: number;
    feedback?: any;
    audio_record_url?: string;
    audio_duration?: number;
    audio_format?: string;
    created_at: string;
    updated_at: string;
}

export interface StudySessionCompletionResponse {
    session_id: string;
    status: string;
    completed_at: string;
    was_already_completed: boolean;
    group: {
        id: string;
        name: string;
        description: string;
        total_vocabularies: number;
    };
    performance_summary: {
        total_attempts: number;
        words_studied: number;
        words_correct: number;
        words_incorrect: number;
        accuracy: number;
        score: number;
        time_spent_seconds: number;
        time_spent_minutes: number;
    };
    attempts: Array<{
        attempt_id: string;
        vocabulary_id: string;
        vocabulary_word: string;
        attempt_number: number;
        score: number;
        max_score: number;
        is_correct: boolean;
        answer: string;
        audio_record_url?: string;
        audio_duration?: number;
        audio_format?: string;
        created_at: string;
        updated_at: string;
        feedback?: any;
    }>;
    session_settings: {
        session_type: string;
        start_time: string;
        end_time?: string;
    };
}

// Group Collection Requests
export interface CreateGroupCollectionRequest {
    name: string;
    description?: string;
    groupIds: string[]; // Array of Group UUIDs
    is_public?: boolean;
    category?: string;
    tags?: string[];
}

export interface UpdateGroupCollectionRequest extends Partial<CreateGroupCollectionRequest> {
    id: string;
}

// Legacy interfaces for backward compatibility
export interface CreateWordRequest {
    word: string;
    pronunciation?: string;
    definition?: string;
    example?: string;
    part_of_speech?: string;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    category?: number;
    audio_url?: string;
    image_url?: string;
}

export interface UpdateWordRequest extends CreateWordRequest {
    id: number;
}

export interface VocabularyFilters {
    search?: string;
    difficulty?: string;
    category?: string;
    part_of_speech?: string;
    isFavorite?: boolean;
    isBookmarked?: boolean;
    groupId?: string; // Group UUID
    masteryLevel?: {
        min?: number;
        max?: number;
    };
}

export interface PaginationParams {
    page?: number;
    limit?: number;
}

export interface VocabularyResponse {
    results: VocabularyWord[];
    count: number;
    next?: string;
    previous?: string;
}

// Legacy FlashcardGroup interfaces
export interface CreateFlashcardGroupRequest {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    vocabularyIds?: number[];
}

export interface UpdateFlashcardGroupRequest extends CreateFlashcardGroupRequest {
    id: number;
}

export interface FlashcardGroupFilters {
    key_word?: string;
    isDefault?: boolean;
}

export interface FlashcardGroupResponse {
    groups: FlashcardGroup[];
    total: number;
    page: number;
    totalPages: number;
}

export interface PracticeResult {
    wordId: string;
    correct: boolean;
    timeSpent: number;
    difficulty: string;
    practiceType: string;
}

// Enhanced word saving and management interfaces
export interface SavedWord {
    id: string;
    wordId: string;
    userId: string;
    groupId?: string;
    savedAt: string;
    notes?: string;
    customTags?: string[];
    studyPriority: 'low' | 'medium' | 'high';
    masteryLevel: number;
    lastStudied?: string;
    nextReview?: string;
}

export interface CreateSavedWordRequest {
    wordId: string;
    groupId?: string;
    notes?: string;
    customTags?: string[];
    studyPriority?: 'low' | 'medium' | 'high';
}

export interface UpdateSavedWordRequest {
    id: string;
    notes?: string;
    customTags?: string[];
    studyPriority?: 'low' | 'medium' | 'high';
    masteryLevel?: number;
}

export interface SavedWordFilters {
    groupId?: string;
    studyPriority?: 'low' | 'medium' | 'high';
    masteryLevel?: number;
    hasNotes?: boolean;
    tags?: string[];
    lastStudiedBefore?: string;
    lastStudiedAfter?: string;
}

export interface SavedWordResponse {
    results: SavedWord[];
    count: number;
    next?: string;
    previous?: string;
}

// Minigame interfaces
export interface MinigameSession {
    id: string;
    gameType: 'learn' | 'match' | 'test' | 'speaking' | 'listening';
    userId: string;
    groupId?: string;
    wordIds: string[];
    settings: MinigameSettings;
    startTime: string;
    endTime?: string;
    score?: number;
    accuracy?: number;
    wordsCorrect: string[];
    wordsIncorrect: string[];
    timeSpent: number;
    isCompleted: boolean;
}

export interface MinigameSettings {
    difficulty: 'easy' | 'medium' | 'hard';
    timeLimit?: number;
    showHints: boolean;
    autoAdvance: boolean;
    soundEnabled: boolean;
    shuffleQuestions: boolean;
}

export interface CreateMinigameSessionRequest {
    gameType: MinigameSession['gameType'];
    groupId?: string;
    wordIds: string[];
    settings: MinigameSettings;
}

export interface UpdateMinigameSessionRequest {
    id: string;
    score?: number;
    accuracy?: number;
    wordsCorrect?: string[];
    wordsIncorrect?: string[];
    timeSpent?: number;
    isCompleted?: boolean;
}

export interface MinigameStats {
    gameType: MinigameSession['gameType'];
    totalSessions: number;
    averageScore: number;
    averageAccuracy: number;
    totalTimeSpent: number;
    bestScore: number;
    improvementRate: number;
    favoriteDifficulty: string;
    strongestCategories: string[];
    weakestCategories: string[];
}

// Progress tracking interfaces
export interface StudyProgress {
    wordId: string;
    masteryLevel: number;
    correctAttempts: number;
    incorrectAttempts: number;
    lastStudied: string;
    nextReview: string;
    studyStreak: number;
    timeSpent: number;
    difficultyProgression: string[];
    confidenceLevel: number;
}

export interface UpdateStudyProgressRequest {
    wordId: string;
    isCorrect: boolean;
    timeSpent: number;
    difficulty: string;
    gameType?: string;
    sessionId?: string;
}

export default new VocabularyService();
